import { Link } from "react-router-dom";
import { Phone, MapPin } from "lucide-react";
import { BUSINESS, NAV_LINKS, SERVICES, SERVICE_AREAS } from "../lib/content";

export const Footer = () => {
  return (
    <footer
      className="bg-[#050505] border-t border-[#27272a] relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-10">
        {/* CTA strip */}
        <div className="mb-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 border-t border-b border-[#27272a] py-12">
          <div>
            <div className="label-sm text-[#f59e0b] mb-3">Ready to start</div>
            <h3 className="font-display font-black text-4xl md:text-5xl uppercase leading-[0.9] max-w-xl">
              Built to last.<br />
              <span className="text-[#f59e0b]">Priced honestly.</span>
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors whitespace-nowrap"
              data-testid="footer-cta-call"
            >
              <Phone className="w-4 h-4" strokeWidth={3} />
              {BUSINESS.phone}
            </a>
            <a
              href={`tel:${BUSINESS.phone2Raw}`}
              className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors whitespace-nowrap"
              data-testid="footer-cta-call-2"
            >
              <Phone className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
              {BUSINESS.phone2}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-16">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 border-2 border-[#f59e0b] flex items-center justify-center">
                <span className="font-display font-black text-[#f59e0b] text-xl leading-none">
                  NW
                </span>
              </div>
              <div className="font-display font-black text-xl uppercase tracking-wide">
                North West<br />Asphalt
              </div>
            </div>
            <p className="text-[#a1a1aa] text-sm leading-relaxed max-w-sm mb-6">
              Family owned since {BUSINESS.established}. Over {BUSINESS.yearsExperience} years of
              reliable paving, repair, and maintenance across Western Canada.
            </p>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b]">
              Family Owned · Est. {BUSINESS.established}
            </div>
          </div>

          {/* Sitemap */}
          <div className="lg:col-span-2">
            <div className="label-sm text-[#f59e0b] mb-5">Navigate</div>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-[#fafaf9] hover:text-[#f59e0b] transition-colors text-sm"
                    data-testid={`footer-link-${link.testId}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <div className="label-sm text-[#f59e0b] mb-5">Services</div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-[#fafaf9] hover:text-[#f59e0b] transition-colors text-sm"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="label-sm text-[#f59e0b] mb-5">Contact</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#f59e0b] mt-0.5 shrink-0" />
                <div className="flex flex-col">
                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    className="hover:text-[#f59e0b] transition-colors"
                    data-testid="footer-phone"
                  >
                    {BUSINESS.phone}
                  </a>
                  <a
                    href={`tel:${BUSINESS.phone2Raw}`}
                    className="hover:text-[#f59e0b] transition-colors"
                    data-testid="footer-phone-2"
                  >
                    {BUSINESS.phone2}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#f59e0b] mt-0.5 shrink-0" />
                <div className="text-[#a1a1aa]">
                  {SERVICE_AREAS.map((a) => a.city).join(" · ")}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-[#27272a] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#a1a1aa]">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} North West Asphalt. All rights reserved.
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              className="hover:text-[#f59e0b] transition-colors uppercase tracking-[0.2em]"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="uppercase tracking-[0.2em]">
              Paving · Sealing · Repairs · Since {BUSINESS.established}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
